import { LogObject, privateAppsAutomaticMinorUpdatesToggleParams, privateAppsAutomaticMinorUpdatesTurnedOnParams, privateAppsAvailableAppsContextMenuClickParams, privateAppsCreateNewAppClickParams, privateAppsInstallAppClickParams, privateAppsInstallAppSuccessParams, privateAppsInstalledAppsContextMenuClickParams, privateAppsLeftTreeContextMenuClickParams, privateAppsPrivateAppsLeftBarClickParams, privateAppsUninstallAppClickParams, privateAppsUninstallAppSuccessParams, privateAppsUpdateAppVersionParams, shareableUrlOpenedParams } from '../types';

/**
 * A function to create a log object for the 'privateAppsInstallAppSuccess' event (evid: 4201)
 *
 * Event description: The event is fired once the installation of a Blocks app finishes successfully
 *
 * @param {privateAppsInstallAppSuccessParams} params - The parameters for this event
 * @property {GUID} appId - app identifier
 * @property {string} appSettings - {widget1:default, widget2:current page,widget3:new page, widget4:lightbox}
 * @property {string} builderType - {classic editor, editorX}
 * @property {boolean} is_latest_version - True if the user installed the latest version, False else
 * @property {string} origin - {context menu, app item, app details page}
 * @property {string} version - The version of the app that the user installed
 * @return {LogObject<privateAppsInstallAppSuccessParams>} LogObject<privateAppsInstallAppSuccessParams>
 */
export function privateAppsInstallAppSuccess(params: privateAppsInstallAppSuccessParams): LogObject<privateAppsInstallAppSuccessParams> {
  return { evid: 4201, src: 110, endpoint: 'blocks-consumption', params };
}

/**
 * A function to create a log object for the 'privateAppsInstallAppClick' event (evid: 4200)
 *
 * Event description: The event is fired once a user clicks on install a Blocks app
 *
 * @param {privateAppsInstallAppClickParams} params - The parameters for this event
 * @property {GUID} appId - app Identifier
 * @property {string} builderType - {classic editor, editorX}
 * @property {string} origin - {context menu, app item, app details page}
 * @return {LogObject<privateAppsInstallAppClickParams>} LogObject<privateAppsInstallAppClickParams>
 */
export function privateAppsInstallAppClick(params: privateAppsInstallAppClickParams): LogObject<privateAppsInstallAppClickParams> {
  return { evid: 4200, src: 110, endpoint: 'blocks-consumption', params };
}

/**
 * A function to create a log object for the 'privateAppsCreateNewAppClick' event (evid: 4202)
 *
 * Event description: The event is fired when the user clicks on create new app link in the private apps panel (fires also on "go to blocks" button in empty state)
 *
 * @param {privateAppsCreateNewAppClickParams} params - The parameters for this event
 * @property {string} builderType - builder_type
 * @property {string} origin - origin
 * @property {GUID} appId - app_id
 * @return {LogObject<privateAppsCreateNewAppClickParams>} LogObject<privateAppsCreateNewAppClickParams>
 */
export function privateAppsCreateNewAppClick(params: privateAppsCreateNewAppClickParams): LogObject<privateAppsCreateNewAppClickParams> {
  return { evid: 4202, src: 110, endpoint: '', params };
}

/**
 * A function to create a log object for the 'privateAppsAvailableAppsContextMenuClick' event (evid: 4203)
 *
 * Event description: The event is fired when the user clicks on one of the options in the available apps context menu
 *
 * @param {privateAppsAvailableAppsContextMenuClickParams} params - The parameters for this event
 * @property {string} builderType - builder_type
 * @property {string} origin - origin
 * @property {GUID} appId - app_id
 * @property {string} action - action
 * @return {LogObject<privateAppsAvailableAppsContextMenuClickParams>} LogObject<privateAppsAvailableAppsContextMenuClickParams>
 */
export function privateAppsAvailableAppsContextMenuClick(params: privateAppsAvailableAppsContextMenuClickParams): LogObject<privateAppsAvailableAppsContextMenuClickParams> {
  return { evid: 4203, src: 110, endpoint: '', params };
}

/**
 * A function to create a log object for the 'privateAppsInstalledAppsContextMenuClick' event (evid: 4204)
 *
 * Event description: The event is fired when the user clicks on one of the options in the available apps context menu
 *
 * @param {privateAppsInstalledAppsContextMenuClickParams} params - The parameters for this event
 * @property {string} builderType - builder_type
 * @property {string} origin - origin
 * @property {GUID} appId - app_id
 * @property {string} action - action
 * @return {LogObject<privateAppsInstalledAppsContextMenuClickParams>} LogObject<privateAppsInstalledAppsContextMenuClickParams>
 */
export function privateAppsInstalledAppsContextMenuClick(params: privateAppsInstalledAppsContextMenuClickParams): LogObject<privateAppsInstalledAppsContextMenuClickParams> {
  return { evid: 4204, src: 110, endpoint: '', params };
}

/**
 * A function to create a log object for the 'privateAppsUpdateAppVersion' event (evid: 4205)
 *
 * Event description: The event is fired when the user clicks on update version in the context menu of an installed app
 *
 * @param {privateAppsUpdateAppVersionParams} params - The parameters for this event
 * @property {string} builderType - builder_type
 * @property {string} version - version
 * @property {string} origin - origin
 * @property {GUID} appId - app_id
 * @return {LogObject<privateAppsUpdateAppVersionParams>} LogObject<privateAppsUpdateAppVersionParams>
 */
export function privateAppsUpdateAppVersion(params: privateAppsUpdateAppVersionParams): LogObject<privateAppsUpdateAppVersionParams> {
  return { evid: 4205, src: 110, endpoint: '', params };
}

/**
 * A function to create a log object for the 'privateAppsUninstallAppClick' event (evid: 4206)
 *
 * Event description: The event is fired when the user clicks on uninstall button in the uninstall modal
 *
 * @param {privateAppsUninstallAppClickParams} params - The parameters for this event
 * @property {GUID} appId - app_id
 * @property {string} builderType - builder_type
 * @return {LogObject<privateAppsUninstallAppClickParams>} LogObject<privateAppsUninstallAppClickParams>
 */
export function privateAppsUninstallAppClick(params: privateAppsUninstallAppClickParams): LogObject<privateAppsUninstallAppClickParams> {
  return { evid: 4206, src: 110, endpoint: '', params };
}

/**
 * A function to create a log object for the 'privateAppsUninstallAppSuccess' event (evid: 4207)
 *
 * Event description: The event is fired when the app was successfully uninstalled
 *
 * @param {privateAppsUninstallAppSuccessParams} params - The parameters for this event
 * @property {GUID} appId - app_id
 * @property {string} builderType - builder_type
 * @return {LogObject<privateAppsUninstallAppSuccessParams>} LogObject<privateAppsUninstallAppSuccessParams>
 */
export function privateAppsUninstallAppSuccess(params: privateAppsUninstallAppSuccessParams): LogObject<privateAppsUninstallAppSuccessParams> {
  return { evid: 4207, src: 110, endpoint: '', params };
}

/**
 * A function to create a log object for the 'privateAppsLeftTreeContextMenuClick' event (evid: 4208)
 *
 * Event description: The event is fired when clicking on specific item in the context menu of the private apps in the left tree
 *
 * @param {privateAppsLeftTreeContextMenuClickParams} params - The parameters for this event
 * @property {string} action - action
 * @property {string} builderType - builder_type
 * @return {LogObject<privateAppsLeftTreeContextMenuClickParams>} LogObject<privateAppsLeftTreeContextMenuClickParams>
 */
export function privateAppsLeftTreeContextMenuClick(params: privateAppsLeftTreeContextMenuClickParams): LogObject<privateAppsLeftTreeContextMenuClickParams> {
  return { evid: 4208, src: 110, endpoint: '', params };
}

/**
 * A function to create a log object for the 'privateAppsAutomaticMinorUpdatesToggle' event (evid: 4209)
 *
 * Event description: The event is fired when turning on and off the minor updates toggle
 *
 * @param {privateAppsAutomaticMinorUpdatesToggleParams} params - The parameters for this event
 * @property {string} builderType - builder_type
 * @property {boolean} toggle - toggle
 * @property {boolean} isLastVersion - is_last_version
 * @property {GUID} appId - app_id
 * @return {LogObject<privateAppsAutomaticMinorUpdatesToggleParams>} LogObject<privateAppsAutomaticMinorUpdatesToggleParams>
 */
export function privateAppsAutomaticMinorUpdatesToggle(params: privateAppsAutomaticMinorUpdatesToggleParams): LogObject<privateAppsAutomaticMinorUpdatesToggleParams> {
  return { evid: 4209, src: 110, endpoint: '', params };
}

/**
 * A function to create a log object for the 'privateAppsAutomaticMinorUpdatesTurnedOn' event (evid: 4210)
 *
 * Event description: The event is fired when clicking on the "turn on" button in the automatic updates modal
 *
 * @param {privateAppsAutomaticMinorUpdatesTurnedOnParams} params - The parameters for this event
 * @property {string} builderType - builder_type
 * @property {boolean} isLastVersion - is_last_version
 * @property {GUID} appId - app_id
 * @return {LogObject<privateAppsAutomaticMinorUpdatesTurnedOnParams>} LogObject<privateAppsAutomaticMinorUpdatesTurnedOnParams>
 */
export function privateAppsAutomaticMinorUpdatesTurnedOn(params: privateAppsAutomaticMinorUpdatesTurnedOnParams): LogObject<privateAppsAutomaticMinorUpdatesTurnedOnParams> {
  return { evid: 4210, src: 110, endpoint: '', params };
}

/**
 * A function to create a log object for the 'privateAppsPrivateAppsLeftBarClick' event (evid: 4211)
 *
 * Event description: The event is fired when clicking on the Private Apps option inside the apps sub menu under the Editors Apps main menu
 *
 * @param {privateAppsPrivateAppsLeftBarClickParams} params - The parameters for this event
 * @property {string} builderType - builder_type
 * @return {LogObject<privateAppsPrivateAppsLeftBarClickParams>} LogObject<privateAppsPrivateAppsLeftBarClickParams>
 */
export function privateAppsPrivateAppsLeftBarClick(params: privateAppsPrivateAppsLeftBarClickParams): LogObject<privateAppsPrivateAppsLeftBarClickParams> {
  return { evid: 4211, src: 110, endpoint: '', params };
}

/**
 * A function to create a log object for the 'shareableUrlOpened' event (evid: 4212)
 *
 * Event description: BI event fired when the shareable URL is opened
 *
 * @param {shareableUrlOpenedParams} params - The parameters for this event
 * @property {string} url - url
 * @property {string} result - result
 * @property {string} component_category - component_category
 * @property {string} errorReason - error_reason
 * @property {string} componentType - component_type
 * @property {boolean} is_wix_app - is_wix_app
 * @property {GUID} appId - app_id
 * @property {string} component_id - component_id
 * @return {LogObject<shareableUrlOpenedParams>} LogObject<shareableUrlOpenedParams>
 */
export function shareableUrlOpened(params: shareableUrlOpenedParams): LogObject<shareableUrlOpenedParams> {
  return { evid: 4212, src: 110, endpoint: '', params };
}
